import React, { FC } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useEventBanner } from "hooks/useEventBanner";
import ButtonExplore from "shared/Button/ButtonExplore";

interface EventBannerProps {
    className?: string;
}

const EventBanner: FC<EventBannerProps> = ({ className = "" }) => {
    const navigate = useNavigate();
    const { liveEvents, isLoading, isVisible } = useEventBanner();

    if (isLoading) {
        return (
            <div className={`w-full rounded-2xl bg-gray-100 dark:bg-neutral-800 animate-pulse h-20 ${className}`} />
        );
    }

    if (!isVisible || !liveEvents || liveEvents.length === 0) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`w-full overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 relative group flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-white/10 ${className}`}
        >
            {/* Glossy Overlay */}
            <div className="absolute inset-0 bg-white/5 backdrop-blur-[2px] pointer-events-none" />

            {liveEvents.map((event, index) => (
                <div
                    key={event.id}
                    className="flex-1 relative group/segment hover:bg-white/5 transition-colors duration-300"
                >
                    <div className="relative px-6 py-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            {/* Icon - only show on larger screens or if space permits */}
                            <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 border border-white/20 backdrop-blur-md group-hover/segment:scale-110 transition-transform duration-500">
                                <Sparkles className="h-5 w-5 text-white" />
                            </div>

                            <div>
                                <div className="flex items-center gap-2 mb-0.5">
                                    <span className="px-1.5 py-0.5 rounded-full bg-emerald-500 text-[8px] font-black text-white uppercase tracking-tighter animate-pulse">
                                        LIVE NOW
                                    </span>
                                    <span className="text-white/40 text-[9px] font-medium uppercase tracking-widest hidden sm:inline">
                                        Event
                                    </span>
                                </div>
                                <h3 className="text-base sm:text-lg font-black text-white leading-tight uppercase tracking-tight truncate max-w-[150px] sm:max-w-none">
                                    {event.name}
                                </h3>
                            </div>
                        </div>

                        <ButtonExplore
                            onClick={() => navigate(`/occasions?eventId=${event.id}&eventName=${encodeURIComponent(event.name)}`)}
                        />
                    </div>
                </div>
            ))}

            {/* Ambient decorations */}
            <div className="absolute top-[-50%] left-[-10%] w-64 h-64 bg-white/10 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute bottom-[-50%] right-[-10%] w-64 h-64 bg-blue-400/10 rounded-full blur-[100px] pointer-events-none" />
        </motion.div>
    );
};

export default EventBanner;
